import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { appsApi } from '@/api/appsApi';
import type { AppListRequest, CreateAppRequest, UpdateAppRequest } from '@/types/app';

const KEYS = {
  all: ['apps'] as const,
  list: (f: AppListRequest) => [...KEYS.all, 'list', f] as const,
  detail: (id: number) => [...KEYS.all, 'detail', id] as const,
};

export function useApps(filters: AppListRequest = {}) {
  return useQuery({ queryKey: KEYS.list(filters), queryFn: () => appsApi.list(filters) });
}

export function useApp(id: number | null) {
  return useQuery({
    queryKey: KEYS.detail(id ?? 0),
    queryFn: () => appsApi.getById(id!),
    enabled: id != null,
  });
}

export function useCreateApp() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (r: CreateAppRequest) => appsApi.create(r),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.all }),
  });
}

export function useUpdateApp() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, request }: { id: number; request: UpdateAppRequest }) =>
      appsApi.update(id, request),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.all }),
  });
}

export function useToggleAppApproval() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, isApprovedInPlay }: { id: number; isApprovedInPlay: boolean }) =>
      appsApi.toggleApproval(id, isApprovedInPlay),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.all }),
  });
}

export function useDeactivateApp() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => appsApi.deactivate(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.all }),
  });
}

export function useReactivateApp() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => appsApi.reactivate(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.all }),
  });
}
